import { FlexSpinner } from '@lovoldsystem/ui'
import { Alert, Box, Stack, Typography, useTheme } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useGetCustomerLicenseRates, useUpdateCustomerLicenseRates } from 'src/api/customer-license'
import { AppTextField } from 'src/components/AppTextField'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { MUIThemeColors } from 'src/mui-theme'
import { ShowDialogProps } from 'src/show-dialog'
import { getThemeColor } from 'src/utils'
import { formatBillingCurrency } from '../licenseContainer.action'
import {
  licenseRateToInputString,
  parseLicenseRatesUpdateBody,
} from './EditCustomerLicenseRatesModal.action'

type EditCustomerLicenseRatesModalProps = ShowDialogProps & {
  customerId: string
  customerName: string
  isGlobalServiceStationProvider: boolean
}

export const EditCustomerLicenseRatesModal = ({
  pop,
  customerId,
  customerName,
  isGlobalServiceStationProvider,
}: EditCustomerLicenseRatesModalProps) => {
  const theme = useTheme()
  const { t } = useTranslation('admin-license')
  const titleColor = getThemeColor(theme, MUIThemeColors.secondaryDark)

  const { data, isLoading, isError } = useGetCustomerLicenseRates(customerId, { enabled: !!customerId })
  const updateMutation = useUpdateCustomerLicenseRates()

  const [locality, setLocality] = useState('')
  const [serviceStation, setServiceStation] = useState('')
  const [storage, setStorage] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)

  useEffect(() => {
    if (!data) return
    setLocality(licenseRateToInputString(data.localityMonthlyRate))
    setServiceStation(licenseRateToInputString(data.serviceStationMonthlyRate))
    setStorage(licenseRateToInputString(data.storageMonthlyRate))
    setValidationError(null)
  }, [data])

  const onSave = useCallback(async () => {
    setValidationError(null)
    const parsed = parseLicenseRatesUpdateBody(isGlobalServiceStationProvider, {
      locality,
      serviceStation,
      storage,
    })
    if (!parsed.ok) {
      setValidationError(t('editLicenseRatesValidationInvalid'))
      return
    }

    try {
      await updateMutation.mutateAsync({
        customerId,
        body: parsed.body,
      })
      toast(t('toastLicenseRatesSaved'), { type: 'success' })
      pop()
    } catch {
      setValidationError(t('editLicenseRatesSaveError'))
    }
  }, [
    customerId,
    isGlobalServiceStationProvider,
    locality,
    serviceStation,
    storage,
    t,
    updateMutation,
    pop,
  ])

  const onResetDefaults = useCallback(() => {
    if (isGlobalServiceStationProvider) {
      setServiceStation('')
    }
    else {
      setLocality('')
      setServiceStation('')
      setStorage('')
    }
    setValidationError(null)
  }, [isGlobalServiceStationProvider])

  return (
    <GenericDialogLayout
      title={
        <Box>
          <Typography variant="h6" sx={{ color: titleColor, fontWeight: 700 }}>
            {t('editLicenseRatesTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            {customerName}
          </Typography>
        </Box>
      }
      pop={pop}
      removedCloseButton
      actions={[
        <GridActionButton key="cancel" onClick={() => pop()} variant="outlined" color="secondary" size="medium" buttonText={t('close')} />,
        <GridActionButton
          key="reset"
          onClick={onResetDefaults}
          variant="outlined"
          color="secondary"
          size="medium"
          disabled={!data}
          buttonText={t('editLicenseRatesClearOverrides')}
        />,
        <GridActionButton
          key="save"
          onClick={() => void onSave()}
          variant="contained"
          color="primary"
          size="medium"
          buttonText={t('editLicenseRatesSave')}
          disabled={isLoading || !data || updateMutation.isLoading}
        />,
      ]}
    >
      <Box sx={{ minHeight: 120, maxHeight: { xs: '70vh', sm: '60vh' }, overflow: 'auto' }}>
        {isLoading && (
          <Box display="flex" justifyContent="center" py={3}>
            <FlexSpinner />
          </Box>
        )}
        {!isLoading && isError && <Alert severity="error">{t('editLicenseRatesLoadError')}</Alert>}
        {!isLoading && !isError && data && (
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {isGlobalServiceStationProvider && (
              <Alert severity="info">{t('editLicenseRatesGlobalProviderHint')}</Alert>
            )}
            {validationError && <Alert severity="error">{validationError}</Alert>}
            <Typography variant="caption" color="text.secondary" component="p" sx={{ m: 0 }}>
              {t('editLicenseRatesDefaultsLine', {
                locality: formatBillingCurrency(data.systemDefaultLocalityMonthly),
                serviceStation: formatBillingCurrency(data.systemDefaultServiceStationMonthly),
                storage: formatBillingCurrency(data.systemDefaultStorageMonthly),
              })}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="p" sx={{ m: 0 }}>
              {t('editLicenseRatesEmptyHint')}
            </Typography>
            {!isGlobalServiceStationProvider && (
              <AppTextField
                label={t('editLicenseRatesLocality')}
                value={locality}
                onChange={e => setLocality(e.target.value)}
                type="text"
                inputMode="decimal"
                fullWidth
              />
            )}
            <AppTextField
              label={t('editLicenseRatesServiceStation')}
              value={serviceStation}
              onChange={e => setServiceStation(e.target.value)}
              type="text"
              inputMode="decimal"
              fullWidth
            />
            {!isGlobalServiceStationProvider && (
              <AppTextField
                label={t('editLicenseRatesStorage')}
                value={storage}
                onChange={e => setStorage(e.target.value)}
                type="text"
                inputMode="decimal"
                fullWidth
              />
            )}
          </Stack>
        )}
      </Box>
    </GenericDialogLayout>
  )
}
